/**
 * Per-tour analytics counters — populates the dashboard tour Overview's
 * stat cards (Total Views / Avg. Time / Shares).
 *
 * Atomic increments live on the tour Firestore doc (analytics.{views,
 * shares, sessionCount, totalSessionMs}) via the tour-analytics-api GCF.
 * Reads also go through the GCF so we don't need to widen Firestore
 * rules for direct client reads of the tour doc.
 */

export interface TourAnalytics {
  views: number;
  shares: number;
  sessionCount: number;
  totalSessionMs: number;
  lastViewAt: string | null;
}

function baseUrl(): string {
  const url = process.env.REACT_APP_ANALYTICS_API_BASE_URL;
  if (!url) {
    throw new Error('REACT_APP_ANALYTICS_API_BASE_URL is not configured');
  }
  return url.replace(/\/$/, '');
}

function stripPrefix(tourId: string): string {
  return tourId.startsWith('tours/') ? tourId.slice(6) : tourId;
}

export async function getTourAnalytics(
  tourId: string,
): Promise<TourAnalytics | null> {
  const url = `${baseUrl()}/tourAnalyticsGet?tourId=${encodeURIComponent(
    stripPrefix(tourId),
  )}&_=${Date.now()}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`analytics GET returned ${res.status} ${res.statusText}`);
  }
  const data = (await res.json()) as TourAnalytics;
  return data;
}

export async function recordTourShare(tourId: string): Promise<void> {
  // Best-effort; we don't want a copy-link click to surface an error if the
  // analytics backend is having a moment.
  try {
    await fetch(`${baseUrl()}/tourAnalyticsShare`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tourId: stripPrefix(tourId) }),
      keepalive: true,
    });
  } catch (err) {
    console.warn('[tourAnalytics] recordTourShare failed', err);
  }
}
